export type CalendarEventStatus = 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

// Mirrors backend-nest/prisma/schema.prisma's CalendarEventSource enum
// exactly. 'provider_sync' is an external-calendar import (e.g. Google) —
// neither a human in this org nor one of our agents created it.
export type CalendarEventSource = 'manual' | 'voice_agent' | 'platform_agent' | 'provider_sync';

/**
 * The layouts the calendar screen can switch between.
 *
 * 'agenda' is not a calendar period like the other three — it is a rolling
 * forward-looking list. Every switch over this union in calendarViewRange.ts
 * is exhaustive with a `never` check, because they all used to end in an
 * unguarded `return <day>` and a new member rendered as Day instead of
 * failing to compile.
 */
export type CalendarView = 'month' | 'week' | 'day' | 'agenda';

/**
 * Statuses a rep can still cancel from. Single source of truth for the
 * "Cancel meeting" action (EventFormModal.tsx) — was duplicated as a local
 * `CANCELLABLE_STATUSES` Set inside EventCard.tsx before EventCard.tsx was
 * removed as dead code once this action moved to the form (Unit 6).
 */
export const CANCELLABLE_EVENT_STATUSES = new Set<CalendarEventStatus>(['scheduled', 'confirmed']);

export type CalendarLeadInviteStatus =
  | 'not_requested'
  | 'pending'
  | 'sent'
  | 'blocked_consent'
  | 'failed';

// Mirrors backend-nest/src/calendar/lead-invite-predicate.ts's
// LeadInviteSuppressedReason exactly — every 'blocked_consent' event carries
// one of these, and the UI must never present the raw value to a rep.
export type CalendarLeadInviteSuppressedReason =
  | 'org_opt_out'
  | 'missing_lead_email'
  | 'do_not_call'
  | 'requires_review'
  | 'lead_unresolvable';

/** Mirrors backend-nest/prisma/schema.prisma's CalendarAttendeeResponse enum. */
export type CalendarAttendeeResponse = 'needs_action' | 'accepted' | 'declined' | 'tentative';

/**
 * A guest on an event.
 *
 * No display name, deliberately: `CalendarEventAttendee.userId` is a bare
 * indexed scalar on the Prisma model, not a relation, so the API cannot join
 * User without a schema migration. `email` is the identity every attendee has
 * — including a provider-pulled external, whose `userId` is null — and the UI
 * resolves a colleague's name from the org members it already holds.
 */
export interface CalendarEventAttendee {
  id: string;
  calendarEventId: string;
  userId: string | null;
  email: string;
  isOrganizer: boolean;
  response: CalendarAttendeeResponse;
  createdAt: string;
}

export interface CalendarEvent {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  location: string | null;
  conferenceUrl: string | null;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  allDay: boolean;
  status: CalendarEventStatus;
  // Optional: the API has always returned it (it's a plain Prisma column),
  // but existing call sites/tests construct CalendarEvent literals without
  // it. Required would break every one of those — see EventFormModal.test.tsx.
  source?: CalendarEventSource;
  leadId: string | null;
  leadEmail: string | null;
  assignedToId: string | null;
  createdById: string | null;
  // Optional for the same reason `source` is: existing call sites and tests
  // construct CalendarEvent literals without it. The API always returns it now
  // (see ATTENDEE_INCLUDE in calendar.service.ts).
  attendees?: CalendarEventAttendee[];
  leadInviteRequested: boolean;
  leadInviteStatus: CalendarLeadInviteStatus;
  inviteSuppressedReason: CalendarLeadInviteSuppressedReason | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventNote {
  id: string;
  calendarEventId: string;
  organizationId: string;
  userId: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalendarEventPayload {
  organizationId: string;
  title: string;
  description?: string;
  location?: string;
  startsAt: string;
  endsAt: string;
  timeZone?: string;
  allDay?: boolean;
  leadId?: string;
  leadEmail?: string;
  assignedToId?: string;
  leadInviteRequested?: boolean;
}

export interface UpdateCalendarEventPayload {
  title?: string;
  description?: string;
  location?: string;
  startsAt?: string;
  endsAt?: string;
  timeZone?: string;
  allDay?: boolean;
  leadId?: string | null;
  leadEmail?: string | null;
  assignedToId?: string | null;
  leadInviteRequested?: boolean;
}
